"use client";

import Link from "next/link";
import {
  ArrowRight,
  BadgeDollarSign,
  Boxes,
  ChevronDown,
  ClipboardCheck,
  CreditCard,
  FileText,
  Headphones,
  Landmark,
  PackageCheck,
  Search,
  ShieldCheck,
  ShoppingBag,
  Sparkles,
  Truck,
  UserRound,
  Warehouse,
  X,
} from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { useUserCenterLocale } from "@/lib/use-user-center-locale";
import {
  buildHelpLocation,
  normalizeHelpQuery,
  readHelpLocation,
  searchHelpArticles,
} from "./help-center-model";

type HelpLink = {
  href: string;
  label: string;
};

type HelpArticle = {
  id: string;
  title: string;
  summary: string;
  stage: string;
  answer: string;
  links: HelpLink[];
  popular?: boolean;
};

type HelpCategory = {
  id: string;
  label: string;
  description: string;
  icon: typeof Sparkles;
  articles: HelpArticle[];
};

const categories: HelpCategory[] = [
  {
    id: "getting-started",
    label: "Getting Started",
    description: "Understand PRVBUY and prepare your first purchase.",
    icon: Sparkles,
    articles: [
      {
        id: "what-is-prvbuy",
        title: "What is PRVBUY?",
        summary: "How the buying-agent service connects Chinese sellers to international shoppers.",
        stage: "Before ordering",
        answer:
          "PRVBUY helps you buy from Chinese marketplaces, receive items at a PRVBUY warehouse, review available QC information, combine items, and arrange international shipping. Purchasing and exception handling are currently handled by PRVBUY agents where no automatic platform connection is available.",
        links: [{ href: "/", label: "Start with a product link" }],
        popular: true,
      },
      {
        id: "paste-product-link",
        title: "How do I paste a product link?",
        summary: "Start an order from a marketplace product page.",
        stage: "Product search",
        answer:
          "Copy the full product-page URL from the seller platform and paste it into the PRVBUY search field. Review the detected product, seller, price and available options before continuing. If the page cannot be read, use DIY Order or ask Support to review the link.",
        links: [
          { href: "/", label: "Paste a link" },
          { href: "/diy-order", label: "Open DIY Order" },
        ],
        popular: true,
      },
      {
        id: "supported-platforms",
        title: "Which shopping platforms are supported?",
        summary: "Common sources include Taobao, 1688, Weidian, Xianyu and other Chinese sellers.",
        stage: "Product search",
        answer:
          "PRVBUY is designed for links from Taobao, 1688, Weidian, Xianyu and other Chinese shopping sources. Link readability and available product details vary by platform. An unreadable or restricted listing can be submitted for manual review by a PRVBUY agent.",
        links: [{ href: "/diy-order", label: "Submit a manual order" }],
      },
      {
        id: "before-ordering",
        title: "What should I prepare before ordering?",
        summary: "Check the link, SKU, quantity, budget and shipping restrictions.",
        stage: "Before item payment",
        answer:
          "Prepare the product link, exact color or size, quantity and any seller instructions. Check whether the item may be restricted for international shipping. Your first payment covers the item-side order; international shipping is selected and paid later after warehouse processing.",
        links: [
          { href: "/prohibited-items", label: "Check prohibited items" },
          { href: "/shipping-estimate", label: "Estimate shipping" },
        ],
      },
    ],
  },
  {
    id: "product-ordering",
    label: "Product & Ordering",
    description: "From link review and SKU selection to seller shipment.",
    icon: ShoppingBag,
    articles: [
      {
        id: "paste-link",
        title: "Paste Link",
        summary: "Open a marketplace listing inside the PRVBUY buying flow.",
        stage: "Product search",
        answer:
          "Paste a complete product URL into the Homepage search. Confirm that the product title, seller and pricing match the original listing. If key details are missing, do not guess—send the link and requested option through DIY Order or Support.",
        links: [{ href: "/", label: "Go to link search" }],
      },
      {
        id: "choose-sku",
        title: "Choose the correct SKU",
        summary: "Select the seller option that matches the color, size or version you need.",
        stage: "Before item payment",
        answer:
          "Choose every required seller option and confirm the quantity. Seller images can represent multiple variants, so use the written SKU labels as the primary reference. Add a clear note if the option needs agent confirmation.",
        links: [{ href: "/cart", label: "Review cart" }],
        popular: true,
      },
      {
        id: "pay-item",
        title: "Pay for an item",
        summary: "Understand what the first payment covers.",
        stage: "Item payment",
        answer:
          "Item payment covers the merchandise and item-side charges shown at checkout. It does not include the final international shipping fee. After payment, the order enters review and purchasing according to its current status.",
        links: [
          { href: "/user-center/orders", label: "View orders" },
          { href: "/user-center/wallet", label: "View wallet" },
        ],
      },
      {
        id: "agent-purchase",
        title: "How agent purchase works",
        summary: "What happens when a PRVBUY agent places the order with the seller.",
        stage: "Purchasing",
        answer:
          "After order review, a PRVBUY agent purchases the selected item from the seller. The agent may contact you through order or Support context if the price, stock, SKU or seller terms have changed. Manual purchasing is normal for sources without a direct ordering connection.",
        links: [
          { href: "/user-center/orders", label: "Track order status" },
          { href: "/user-center/support", label: "Contact Support" },
        ],
      },
      {
        id: "order-review",
        title: "Why is my order under review?",
        summary: "Common checks before an agent purchases the item.",
        stage: "Order review",
        answer:
          "Review may be needed for unclear SKUs, seller price changes, restricted-item risk, missing notes or payment verification. PRVBUY agents currently handle these checks. Watch your order timeline and notifications for a requested action.",
        links: [
          { href: "/user-center/orders", label: "Open orders" },
          { href: "/user-center", label: "View notifications" },
        ],
      },
      {
        id: "seller-shipping",
        title: "Seller shipment to the warehouse",
        summary: "What to expect after the agent has purchased your item.",
        stage: "Seller shipped",
        answer:
          "The seller sends the item to the PRVBUY warehouse. Domestic delivery time depends on the seller and carrier. The order status updates when shipment information is available and again after the warehouse receives and registers the item.",
        links: [{ href: "/user-center/orders", label: "Track the order" }],
      },
      {
        id: "cancel-order",
        title: "Can I cancel an order?",
        summary: "Cancellation depends on the order stage and seller response.",
        stage: "Before or during purchasing",
        answer:
          "Open the order and use the available cancellation action if the current status allows it. Once the seller has accepted or shipped the order, cancellation may require an after-sales request and seller approval. A submitted request does not guarantee seller acceptance.",
        links: [
          { href: "/user-center/orders", label: "Open orders" },
          { href: "/user-center/after-sales", label: "Open after-sales" },
        ],
      },
      {
        id: "item-payment-retry",
        title: "Item payment failed or needs retry",
        summary: "Safe steps when checkout does not complete.",
        stage: "Item payment",
        answer:
          "First check the order status and wallet or payment record before paying again. If the order still shows unpaid, retry from the order payment page with an available method. If a charge appears without a matching order update, contact Support and include the order number and payment time.",
        links: [
          { href: "/user-center/orders", label: "Check order status" },
          { href: "/user-center/support?topic=payment", label: "Get payment help" },
        ],
        popular: true,
      },
    ],
  },
  {
    id: "warehouse-qc",
    label: "Warehouse & QC",
    description: "Receiving, inspection photos, dimensions and item issues.",
    icon: Warehouse,
    articles: [
      {
        id: "warehouse-arrival",
        title: "What happens when an item reaches the warehouse?",
        summary: "The receiving and registration flow before storage.",
        stage: "Warehouse received",
        answer:
          "Warehouse staff match the inbound shipment to your order, register the item and record available receiving data. The item then moves into QC processing. Unmatched or visibly abnormal inbound goods require manual investigation by PRVBUY agents.",
        links: [{ href: "/user-center/warehouse", label: "Open warehouse" }],
        popular: true,
      },
      {
        id: "qc-photos",
        title: "QC Photos",
        summary: "How to review the inspection images attached to your item.",
        stage: "Quality inspection",
        answer:
          "Open the warehouse item or order detail to view the QC photos available for that item. Photos show the received item and visible condition; they do not prove hidden performance, authenticity or long-term durability. Use after-sales or Support if the images show a problem.",
        links: [
          { href: "/user-center/warehouse", label: "View warehouse items" },
          { href: "/user-center/after-sales", label: "Request after-sales" },
        ],
      },
      {
        id: "qc-statuses",
        title: "QC Pending and QC Approved",
        summary: "What the two main inspection states mean.",
        stage: "Quality inspection",
        answer:
          "QC Pending means inspection has not reached a final recorded result. QC Approved means the recorded inspection passed the current warehouse check and the item can continue toward storage or packing when eligible. A QC issue requires review before normal parcel submission.",
        links: [{ href: "/user-center/warehouse", label: "Check QC status" }],
      },
      {
        id: "weight-dimensions-storage-time",
        title: "Weight, dimensions and storage time",
        summary: "Where warehouse measurements appear and why they can change.",
        stage: "Stored",
        answer:
          "The warehouse item shows recorded weight, dimensions and receiving time when available. Packing materials and consolidation can change the parcel's final chargeable weight. Use the storage countdown shown in your account as the current source of truth for that item.",
        links: [
          { href: "/user-center/warehouse", label: "View measurements" },
          { href: "/shipping-estimate", label: "Estimate shipping" },
        ],
      },
      {
        id: "warehouse-item-issue",
        title: "The received item has an issue",
        summary: "What to do about a wrong, damaged or incomplete item.",
        stage: "QC issue",
        answer:
          "Review the QC photos and notes, then open the related order or warehouse item. Submit an after-sales request within the available window and describe the issue clearly. Upload evidence when the form allows it; PRVBUY agents coordinate with the seller.",
        links: [
          { href: "/user-center/after-sales", label: "Start after-sales" },
          { href: "/user-center/support", label: "Contact Support" },
        ],
        popular: true,
      },
      {
        id: "apply-after-sales",
        title: "How to apply for after-sales from QC",
        summary: "Use the real order or warehouse context for faster review.",
        stage: "QC issue / Stored",
        answer:
          "Open the relevant order detail or QC view and choose the available after-sales action. Add a specific reason and supporting evidence. If no after-sales action is available for the current state, contact Support with the order and warehouse item identifiers.",
        links: [
          { href: "/user-center/orders", label: "Open order detail" },
          { href: "/user-center/after-sales", label: "View cases" },
        ],
      },
    ],
  },
  {
    id: "storage-parcel",
    label: "Storage & Parcel",
    description: "Store items, consolidate them and prepare an international parcel.",
    icon: Boxes,
    articles: [
      {
        id: "free-storage",
        title: "How free storage works",
        summary: "Understand the item storage window before parcel submission.",
        stage: "Stored",
        answer:
          "Eligible warehouse items receive the free storage period shown in your account. The item-level countdown is the current operational reference. Submit packing before the available period ends, or contact Support early if an exception prevents action.",
        links: [{ href: "/user-center/warehouse", label: "Check storage time" }],
        popular: true,
      },
      {
        id: "consolidation",
        title: "Combine multiple items into one parcel",
        summary: "Select eligible stored goods for consolidated packing.",
        stage: "Ready to ship",
        answer:
          "Select eligible warehouse items and start Submit Packing. Items with unresolved QC, after-sales, restriction or storage issues may not be available for consolidation. Final packing depends on size, compatibility and shipping-line rules.",
        links: [
          { href: "/user-center/warehouse", label: "Select items" },
          { href: "/submit-packing", label: "Submit packing" },
        ],
      },
      {
        id: "submit-packing",
        title: "Submit Packing",
        summary: "Turn selected warehouse items into a parcel request.",
        stage: "Ready to ship",
        answer:
          "Choose eligible items, confirm packing preferences and submit the request. Warehouse staff then prepare the parcel and record its packed information. Submission is not the same as international shipment; shipping selection and payment follow.",
        links: [{ href: "/submit-packing", label: "Open Submit Packing" }],
      },
      {
        id: "premium-services",
        title: "Premium Services",
        summary: "Request available value-added packing options.",
        stage: "Packing request",
        answer:
          "Available services may include packaging or handling options shown in Submit Packing. Choose only the services displayed for your request. Special requirements that are not listed are currently reviewed manually by PRVBUY agents through Support.",
        links: [
          { href: "/submit-packing", label: "View packing options" },
          { href: "/user-center/support?topic=parcel", label: "Ask about a special request" },
        ],
      },
      {
        id: "parcel-created",
        title: "Parcel Created",
        summary: "What this status means after a packing request.",
        stage: "Parcel created",
        answer:
          "Parcel Created means a parcel record now groups the selected items. The warehouse may still be packing or confirming final measurements. Open the parcel for its current state and next available action.",
        links: [{ href: "/user-center/parcels", label: "View parcels" }],
      },
      {
        id: "packing-status",
        title: "Packing and Packed statuses",
        summary: "Follow warehouse work before shipping payment.",
        stage: "Packing / Packed",
        answer:
          "Packing means warehouse preparation is in progress. Packed means final parcel information is available for the next shipping step. If a parcel remains in progress beyond the displayed operational expectation, contact Support with the parcel number.",
        links: [
          { href: "/user-center/parcels", label: "Check parcel status" },
          { href: "/user-center/support?topic=parcel", label: "Contact Support" },
        ],
      },
    ],
  },
  {
    id: "shipping",
    label: "Shipping",
    description: "Estimate, choose, pay, track and receive international shipping.",
    icon: Truck,
    articles: [
      {
        id: "shipping-estimate",
        title: "Shipping Estimate",
        summary: "Get a planning estimate before the final parcel is packed.",
        stage: "Before packing",
        answer:
          "Use the Shipping Estimate tool with destination, category, weight and dimensions. Results are planning estimates, not a final quote. The packed parcel, chargeable weight, available routes and restrictions determine the payable amount.",
        links: [{ href: "/shipping-estimate", label: "Open Shipping Estimate" }],
        popular: true,
      },
      {
        id: "shipping-lines",
        title: "Choose a shipping line",
        summary: "Compare routes by eligibility, speed and charging method.",
        stage: "Shipping selection",
        answer:
          "Review the lines offered for the packed parcel. Availability depends on destination, item type, weight, dimensions and carrier rules. Sensitive goods may have fewer routes or require manual confirmation.",
        links: [
          { href: "/user-center/parcels", label: "Open parcel" },
          { href: "/prohibited-items", label: "Review restrictions" },
        ],
      },
      {
        id: "pay-shipping",
        title: "Pay international shipping",
        summary: "Complete the second payment after packing.",
        stage: "Shipping payment",
        answer:
          "Open the packed parcel, confirm the destination and available line, then pay the displayed international shipping amount. This payment is separate from item payment. Check the parcel status before retrying a payment.",
        links: [
          { href: "/user-center/parcels", label: "View parcels" },
          { href: "/user-center/wallet", label: "View wallet" },
        ],
      },
      {
        id: "international-shipping",
        title: "International shipping process",
        summary: "What happens between payment and carrier delivery.",
        stage: "Paid / Fulfillment",
        answer:
          "After shipping payment, PRVBUY prepares carrier handoff and records shipment information when available. Transit, customs and last-mile delivery are handled by the selected logistics providers and local authorities.",
        links: [{ href: "/user-center/parcels", label: "Track parcel progress" }],
      },
      {
        id: "tracking",
        title: "View a parcel",
        summary: "Where to find the recorded tracking number and parcel lifecycle.",
        stage: "Shipped",
        answer:
          "Open Parcels to review PRVBUY's recorded parcel lifecycle and the tracking number when one has been added. Real-time carrier scan events are not integrated. Contact Support with the parcel and tracking numbers if you need carrier help.",
        links: [
          { href: "/user-center/parcels", label: "View parcels" },
          { href: "/user-center/support?topic=parcel", label: "Get tracking help" },
        ],
        popular: true,
      },
      {
        id: "delivered",
        title: "Delivered status",
        summary: "Check the parcel promptly after carrier delivery.",
        stage: "Delivered",
        answer:
          "Delivered reflects the recorded carrier or parcel lifecycle result. Inspect the outer parcel and contents as soon as possible. Keep packaging, photos and carrier evidence if anything is missing or damaged, then contact Support or open after-sales where applicable.",
        links: [
          { href: "/user-center/parcels", label: "View parcel history" },
          { href: "/user-center/after-sales", label: "Open after-sales" },
        ],
      },
      {
        id: "shipping-difference",
        title: "Why is there a shipping fee difference?",
        summary: "Final carrier data can differ from the prepayment basis.",
        stage: "After shipping payment",
        answer:
          "A difference can result from final chargeable weight, dimensions, packing, route or carrier billing. PRVBUY records the adjustment for review. If an additional payment or refund is required, follow the parcel or wallet record; agents currently handle exceptions manually.",
        links: [
          { href: "/user-center/parcels", label: "Review parcel charges" },
          { href: "/user-center/support?topic=payment", label: "Ask about a difference" },
        ],
      },
      {
        id: "restricted-sensitive",
        title: "Prohibited and sensitive items",
        summary: "Check restrictions before purchase and again before shipping.",
        stage: "Before ordering / Shipping selection",
        answer:
          "Some goods cannot be accepted or may only use specific routes. Restrictions vary by item, carrier and destination. Review the current policy before ordering; PRVBUY agents may request more information or reject a route that cannot legally or safely carry the item.",
        links: [{ href: "/prohibited-items", label: "Read prohibited items policy" }],
      },
    ],
  },
  {
    id: "payment-wallet",
    label: "Payment & Wallet",
    description: "Item payments, shipping payments, balance, top-up and refunds.",
    icon: CreditCard,
    articles: [
      {
        id: "item-payment",
        title: "Item payment",
        summary: "Pay for goods before the agent purchases from the seller.",
        stage: "Checkout",
        answer:
          "Review the SKU, quantity and payable item amount before confirming. Item payment and international shipping payment are separate. The order status is the source of truth for whether payment moved the order forward.",
        links: [{ href: "/user-center/orders", label: "Open orders" }],
      },
      {
        id: "shipping-payment",
        title: "International shipping payment",
        summary: "Pay after the warehouse has prepared the parcel.",
        stage: "Parcel checkout",
        answer:
          "The international shipping amount becomes payable from the parcel flow after packing information and available routes are ready. Confirm the delivery address and shipping option before paying.",
        links: [{ href: "/user-center/parcels", label: "Open parcels" }],
      },
      {
        id: "wallet-balance",
        title: "Wallet Balance",
        summary: "Understand the balance and posted transaction records.",
        stage: "Account",
        answer:
          "Your wallet shows the current balance derived from posted wallet ledger entries. Review the transaction list after payments, top-ups or wallet refunds. If a transaction is missing, contact Support rather than repeating a payment immediately.",
        links: [{ href: "/user-center/wallet", label: "Open wallet" }],
        popular: true,
      },
      {
        id: "top-up",
        title: "Top Up",
        summary: "Wallet funding is currently unavailable because no provider is connected.",
        stage: "Wallet funding",
        answer:
          "Top Up is currently unavailable and will not credit your Wallet balance. When a funding provider becomes available, the current availability and next steps will be shown in Wallet.",
        links: [{ href: "/user-center/wallet", label: "View wallet availability" }],
      },
      {
        id: "card-digital-wallets",
        title: "Wallet payment",
        summary: "The payment method currently supported by PRVBUY.",
        stage: "Checkout",
        answer:
          "PRVBUY currently supports payment from available Wallet balance. The server recalculates and confirms the payable amount before charging it. External card and wallet providers are not connected.",
        links: [
          { href: "/user-center/orders", label: "Check item payment" },
          { href: "/user-center/parcels", label: "Check shipping payment" },
        ],
      },
      {
        id: "refund-to-wallet",
        title: "Refund to wallet",
        summary: "How an approved wallet refund appears.",
        stage: "Refund processing",
        answer:
          "When an approved refund is paid to the PRVBUY wallet, a posted refund entry updates your balance. The after-sales case and wallet ledger provide the current recorded result.",
        links: [
          { href: "/user-center/after-sales", label: "View refund case" },
          { href: "/user-center/wallet", label: "View wallet records" },
        ],
      },
      {
        id: "original-payment-refund",
        title: "Original payment refund records",
        summary: "Understand the current record when a refund uses the original method.",
        stage: "Refund processing",
        answer:
          "PRVBUY can record an approved refund as returned to the original payment method. The current system record does not by itself promise a live payment-provider transfer or arrival time; payment-channel execution and reconciliation are currently handled by PRVBUY agents.",
        links: [
          { href: "/user-center/after-sales", label: "View refund status" },
          { href: "/user-center/support?topic=payment", label: "Ask about a refund" },
        ],
      },
    ],
  },
  {
    id: "refunds-after-sales",
    label: "Refunds & After-Sales",
    description: "Request help, add evidence and follow refund decisions.",
    icon: BadgeDollarSign,
    articles: [
      {
        id: "return-exchange",
        title: "Return or Exchange",
        summary: "Request seller-side resolution for an eligible item issue.",
        stage: "After purchase / QC issue",
        answer:
          "Submit an after-sales request with a clear reason and evidence. Return and exchange details currently share the case description, so state the desired resolution explicitly. Seller acceptance, timing and domestic return conditions can affect the outcome.",
        links: [
          { href: "/user-center/after-sales", label: "Open after-sales" },
          { href: "/returns-and-refunds", label: "Read policy" },
        ],
        popular: true,
      },
      {
        id: "after-sales-request",
        title: "Submit an after-sales request",
        summary: "Create a case from the related order or QC context.",
        stage: "Eligible after-sales window",
        answer:
          "Open the related order, QC view or After-Sales page. Enter a specific reason and submit the request. The case is linked to the order, and PRVBUY agents review it with the seller or operational team.",
        links: [{ href: "/user-center/after-sales", label: "Start a request" }],
      },
      {
        id: "supplement-case",
        title: "Add more information to a case",
        summary: "Supplement the request when agents need clarification.",
        stage: "Case review",
        answer:
          "Open the case detail and use the available supplement action. Add only information relevant to the issue, such as the affected quantity, visible defect, missing part or requested outcome.",
        links: [{ href: "/user-center/after-sales", label: "View cases" }],
      },
      {
        id: "upload-evidence",
        title: "Upload evidence",
        summary: "Attach photos or files that help agents review the issue.",
        stage: "Request / Case review",
        answer:
          "Use the case evidence upload when available. Choose clear files that show the full item and the specific problem. Evidence cannot currently be deleted or replaced through a dedicated customer action, so review the selected file before upload.",
        links: [{ href: "/user-center/after-sales", label: "Open case evidence" }],
      },
      {
        id: "after-sales-status",
        title: "After-sales statuses",
        summary: "Follow requested, approved, rejected and paid outcomes.",
        stage: "Case lifecycle",
        answer:
          "Requested means the case is awaiting review. Approved means the refund decision was accepted but may not be paid yet. Rejected means it was not approved or a cancellable request was closed. Paid means the recorded refund payout step is complete.",
        links: [{ href: "/user-center/after-sales", label: "Check case status" }],
      },
      {
        id: "refund-approval",
        title: "How refund approval works",
        summary: "Agents review eligibility, evidence and seller response.",
        stage: "Case review",
        answer:
          "PRVBUY agents review the order, evidence, item status and seller response before recording a decision. Approval does not always mean immediate arrival in an external payment account; check the selected refund method and subsequent records.",
        links: [{ href: "/user-center/after-sales", label: "View refund timeline" }],
      },
      {
        id: "cancel-after-sales",
        title: "Cancel an after-sales request",
        summary: "Cancellation is available only in the initial request state.",
        stage: "Requested",
        answer:
          "Open the case and use Cancel only when the action is available. A request that has already moved beyond the cancellable state cannot be closed by the customer through the current interface.",
        links: [{ href: "/user-center/after-sales", label: "Open after-sales" }],
      },
    ],
  },
  {
    id: "account-security",
    label: "Account & Security",
    description: "Access, addresses, profile, notifications and account protection.",
    icon: UserRound,
    articles: [
      {
        id: "login-register",
        title: "Log in or create an account",
        summary: "Use one account for orders, warehouse items, parcels and Support.",
        stage: "Account access",
        answer:
          "Create an account with the registration flow or sign in with your existing credentials. Customer records are scoped to the active session, so use the same account throughout the purchase and shipping lifecycle.",
        links: [
          { href: "/login", label: "Log in" },
          { href: "/register", label: "Register" },
        ],
      },
      {
        id: "address-management",
        title: "Manage delivery addresses",
        summary: "Add, edit and choose the correct international delivery address.",
        stage: "Account / Parcel checkout",
        answer:
          "Manage saved addresses in Settings and confirm the selected address again during parcel checkout. Enter the recipient, phone, country, region, city, street lines and postal code accurately.",
        links: [{ href: "/user-center/settings", label: "Open address settings" }],
        popular: true,
      },
      {
        id: "profile",
        title: "Update profile information",
        summary: "Edit the account fields supported by current settings.",
        stage: "Account",
        answer:
          "Open Settings to review and edit the profile information currently supported by the account API. If a field has no edit control, contact Support rather than assuming it can be changed automatically.",
        links: [{ href: "/user-center/settings", label: "Open settings" }],
      },
      {
        id: "notifications",
        title: "Notifications",
        summary: "Follow operational updates linked to your account.",
        stage: "Entire order lifecycle",
        answer:
          "PRVBUY in-site notifications can point to orders, parcels, warehouse items, wallet records, refunds or Support. Open User Center to review recent messages and mark notifications as read.",
        links: [{ href: "/user-center", label: "View notifications" }],
      },
      {
        id: "account-security",
        title: "Protect your account",
        summary: "Keep credentials private and review unexpected activity.",
        stage: "Account",
        answer:
          "Use a unique password, do not share sign-in details, and verify that you are on the PRVBUY site before entering credentials. If you notice unexpected activity or cannot access the account, contact Support with non-sensitive identifying context. Never send passwords or full payment credentials in a ticket.",
        links: [{ href: "/user-center/support?topic=other", label: "Contact Support" }],
      },
    ],
  },
  {
    id: "policies",
    label: "Policies",
    description: "Read the rules that apply to goods, returns, shipping and privacy.",
    icon: FileText,
    articles: [
      {
        id: "disclaimers",
        title: "Service disclaimers",
        summary: "Understand marketplace, inspection and logistics boundaries.",
        stage: "Before ordering",
        answer:
          "Seller content, marketplace availability, inspection visibility, carrier performance and customs decisions involve third parties. PRVBUY provides the service records and actions described in the applicable terms without promising outcomes outside its control.",
        links: [{ href: "/terms", label: "Read Terms of Service" }],
      },
      {
        id: "prohibited-items-policy",
        title: "Prohibited Items",
        summary: "Goods that cannot be accepted or shipped on available routes.",
        stage: "Before ordering / Shipping",
        answer:
          "Review the prohibited-items policy before purchasing. Carrier and destination rules can also restrict sensitive goods that are not universally prohibited. PRVBUY may require manual review or decline handling when a safe, lawful route is unavailable.",
        links: [{ href: "/prohibited-items", label: "Read prohibited items policy" }],
        popular: true,
      },
      {
        id: "return-policy",
        title: "Return and Exchange Policy",
        summary: "Eligibility, evidence and seller-side limitations.",
        stage: "After purchase",
        answer:
          "The policy explains the basis for eligible return, exchange and refund requests. Actual outcomes can depend on item condition, evidence, timing, seller rules and shipping status.",
        links: [{ href: "/returns-and-refunds", label: "Read returns and refunds" }],
      },
      {
        id: "shipping-terms",
        title: "Shipping Terms",
        summary: "Rules for routes, estimates, customs and delivery.",
        stage: "Packing / Shipping",
        answer:
          "Shipping terms cover estimates, carrier selection, chargeable weight, restrictions, customs and delivery responsibilities. Review them before paying international shipping.",
        links: [{ href: "/shipping-policy", label: "Read shipping policy" }],
      },
      {
        id: "privacy",
        title: "Privacy",
        summary: "How PRVBUY describes the handling of personal information.",
        stage: "Account",
        answer:
          "Read the Privacy Policy for the current description of information handling. Avoid sending passwords, full card details or unrelated personal records through Support.",
        links: [{ href: "/privacy", label: "Read Privacy Policy" }],
      },
      {
        id: "terms",
        title: "Terms",
        summary: "The general conditions for using PRVBUY.",
        stage: "Account / Before ordering",
        answer:
          "The Terms of Service describe the conditions that apply when you use PRVBUY. Review them together with the specific shipping, return and prohibited-item policies.",
        links: [{ href: "/terms", label: "Read Terms of Service" }],
      },
    ],
  },
];

const zhCategoryCopy: Record<string, [string, string]> = {
  "getting-started": ["开始使用", "了解 PRVBUY，并为首次购买做好准备。"],
  "product-ordering": ["商品与下单", "从链接确认、规格选择到卖家发货。"],
  "warehouse-qc": ["仓库与质检", "入库、质检照片、尺寸和商品异常处理。"],
  "storage-parcel": ["仓储与包裹", "保管商品、合箱并准备国际包裹。"],
  shipping: ["国际物流", "估算、选择、支付、跟踪并签收国际物流。"],
  "payment-wallet": ["支付与钱包", "商品支付、运费支付、余额、充值和退款。"],
  "refunds-after-sales": ["退款与售后", "提交申请、补充凭证并跟进退款决定。"],
  "account-security": ["账户与安全", "登录、地址、资料、通知和账户保护。"],
  policies: ["政策规则", "了解商品、退换、运输和隐私相关规则。"],
};

const zhArticleCopy: Record<string, [string, string, string]> = {
  "what-is-prvbuy": ["PRVBUY 是什么？", "PRVBUY 帮助您从中国电商平台购买商品、收货入库、查看质检资料、合箱并安排国际运输。暂未自动接入的平台环节由运营人员处理。", "下单前"],
  "paste-product-link": ["如何粘贴商品链接？", "复制卖家平台的完整商品链接并粘贴到 PRVBUY 搜索框。确认商品、卖家、价格和规格后继续；无法读取时可使用自主订单或联系客服。", "商品搜索"],
  "supported-platforms": ["支持哪些购物平台？", "PRVBUY 支持淘宝、1688、微店、闲鱼及其他中国购物来源。不同平台可读取的信息不同，受限商品可提交人工审核。", "商品搜索"],
  "before-ordering": ["下单前需要准备什么？", "请准备商品链接、准确的颜色或尺码、数量和卖家备注，并确认是否存在国际运输限制。商品付款与后续国际运费分开支付。", "商品付款前"],
  "paste-link": ["粘贴链接", "在首页粘贴完整商品网址，并核对标题、卖家和价格。关键信息缺失时，请使用自主订单或联系客服确认。", "商品搜索"],
  "choose-sku": ["选择正确的规格", "选择全部必填规格并确认数量。应以文字规格为主要依据；需要运营确认时请填写清楚的备注。", "商品付款前"],
  "pay-item": ["支付商品款", "首次付款包含结账页显示的商品及商品侧费用，不包含最终国际运费。付款后订单进入审核和采购流程。", "商品付款"],
  "agent-purchase": ["代购采购如何进行", "订单审核后，PRVBUY 运营人员会向卖家采购。价格、库存、规格或卖家条款变化时，可能需要您确认。", "采购中"],
  "order-review": ["为什么订单正在审核？", "规格不清、卖家改价、禁运风险、备注缺失或付款核验都可能触发审核。请关注订单时间线和通知。", "订单审核"],
  "seller-shipping": ["卖家发货到仓库", "采购完成后卖家将商品寄往 PRVBUY 仓库。国内运输时间取决于卖家和承运商，入库登记后状态会再次更新。", "卖家已发货"],
  "cancel-order": ["可以取消订单吗？", "订单状态允许时可在详情页取消。卖家接单或发货后通常需要发起售后并等待卖家同意。", "采购前或采购中"],
  "item-payment-retry": ["商品付款失败或需要重试", "重付前先检查订单状态及钱包或支付记录。出现扣款但订单未更新时，请携带订单号和付款时间联系客服。", "商品付款"],
  "warehouse-arrival": ["商品到达仓库后会怎样？", "仓库会匹配入库件、登记商品和收货资料，然后进入质检。无法匹配或明显异常的包裹会转人工调查。", "仓库已收货"],
  "qc-photos": ["质检照片", "在仓库商品或订单详情中查看质检照片。照片仅反映可见外观，不能证明隐藏性能、真伪或长期耐用性。", "质量检查"],
  "qc-statuses": ["待质检与质检通过", "待质检表示尚无最终结果；质检通过表示已通过当前仓库检查，可在符合条件时进入仓储或打包。", "质量检查"],
  "weight-dimensions-storage-time": ["重量、尺寸和仓储时间", "仓库商品会显示已记录的重量、尺寸和入库时间。包装和合箱可能改变最终计费重量，请以账户中的仓储倒计时为准。", "已入库"],
  "warehouse-item-issue": ["入库商品存在问题", "查看质检照片和备注后，在有效期内提交售后申请并说明问题；表单允许时请上传证据。", "质检异常"],
  "apply-after-sales": ["如何从质检发起售后", "打开对应订单或质检详情，选择可用的售后操作并填写原因与凭证。没有入口时请联系客服并提供相关编号。", "质检异常／已入库"],
  "free-storage": ["免费仓储如何计算", "符合条件的商品享有账户所示免费仓储期。请在到期前提交打包；存在异常时应尽早联系客服。", "已入库"],
  consolidation: ["将多个商品合并为一个包裹", "选择符合条件的仓库商品并提交打包。存在质检、售后、限制或仓储异常的商品可能无法合箱。", "可提交寄送"],
  "submit-packing": ["提交打包", "选择商品和打包偏好后提交申请。仓库完成打包并记录包裹资料后，您还需选择并支付国际物流。", "可提交寄送"],
  "premium-services": ["增值服务", "可选包装或处理服务会显示在提交打包页面。未列出的特殊要求需通过客服人工确认。", "打包申请"],
  "parcel-created": ["包裹已创建", "表示所选商品已组成包裹记录，但仓库可能仍在打包或确认最终尺寸。请打开包裹查看当前状态。", "包裹已创建"],
  "packing-status": ["打包中与已打包", "打包中表示仓库正在处理；已打包表示最终包裹信息可用于下一步物流。超出预期时请携带包裹号联系客服。", "打包中／已打包"],
  "shipping-estimate": ["运费估算", "输入目的地、品类、重量和尺寸可获得规划估算。最终费用取决于打包后的计费重量、可用线路和限制。", "打包前"],
  "shipping-lines": ["选择物流线路", "请按资格、时效和计费方式比较可用线路。线路受目的地、品类、重量、尺寸和承运商规则影响。", "选择物流"],
  "pay-shipping": ["支付国际运费", "打开已打包包裹，确认地址和线路后支付国际运费。重试付款前请先检查包裹状态。", "物流付款"],
  "international-shipping": ["国际运输流程", "支付运费后，PRVBUY 安排交接并记录物流信息。运输、海关及末端配送由对应物流商和当地机构处理。", "已付款／履约中"],
  tracking: ["查看包裹物流", "在包裹中心查看生命周期和已录入的运单号。目前未接入实时承运商轨迹；需要协助时请提供包裹号和运单号。", "已发货"],
  delivered: ["已签收状态", "签收后请尽快检查外箱和商品。缺失或破损时保留包装、照片及承运商凭证，并联系客服或发起适用的售后。", "已签收"],
  "shipping-difference": ["为什么会有运费差额？", "最终计费重量、尺寸、包装、线路或承运商账单可能造成差额。需要补款或退款时请按包裹或钱包记录操作。", "支付运费后"],
  "restricted-sensitive": ["禁运与敏感商品", "部分商品无法收取或只能使用特定线路，规则会因商品、承运商和目的地而异。下单前请查看最新政策。", "下单前／选择物流"],
  "item-payment": ["商品付款", "确认规格、数量和应付商品金额后再付款。商品付款与国际运费分开，订单状态是付款是否生效的依据。", "结账"],
  "shipping-payment": ["国际运费付款", "打包资料和可用线路就绪后，可在包裹流程中支付国际运费。付款前请确认地址和线路。", "包裹结账"],
  "wallet-balance": ["钱包余额", "钱包余额由已入账流水计算。付款、充值或退款后请检查交易列表，记录缺失时不要重复付款，应联系客服。", "账户"],
  "top-up": ["充值", "由于尚未接入充值服务商，当前充值不会增加钱包余额。可用后会在钱包中显示入口和步骤。", "钱包充值"],
  "card-digital-wallets": ["钱包支付", "PRVBUY 当前支持使用可用钱包余额付款，服务端会重新计算并确认金额。暂未接入外部银行卡或电子钱包。", "结账"],
  "refund-to-wallet": ["退款到钱包", "批准的退款入账至 PRVBUY 钱包后，会生成退款流水并更新余额，可结合售后案件和钱包明细核对。", "退款处理中"],
  "original-payment-refund": ["原支付方式退款记录", "系统可记录退款至原支付方式，但记录本身不代表支付渠道已实时到账；渠道执行与对账目前由运营人员处理。", "退款处理中"],
  "return-exchange": ["退货或换货", "请提交原因和证据，并明确希望退货还是换货。结果受卖家同意、时效和国内退回条件影响。", "采购后／质检异常"],
  "after-sales-request": ["提交售后申请", "从对应订单、质检页面或售后中心提交具体原因。案件会关联订单，并由运营人员与卖家或相关团队处理。", "可申请售后的期限内"],
  "supplement-case": ["补充售后资料", "在案件详情中使用补充功能，仅添加与问题相关的信息，例如数量、缺陷、缺件或期望结果。", "案件审核"],
  "upload-evidence": ["上传凭证", "请上传能展示完整商品和具体问题的清晰文件。当前客户侧无法单独删除或替换凭证，上传前请确认。", "申请／案件审核"],
  "after-sales-status": ["售后状态", "已申请表示等待审核；已批准表示决定通过但可能尚未付款；已拒绝表示未通过或已关闭；已付款表示退款支付步骤已记录完成。", "案件流程"],
  "refund-approval": ["退款如何审核", "运营人员会结合订单、证据、商品状态和卖家回复作出决定。批准不一定代表外部支付账户立即到账。", "案件审核"],
  "cancel-after-sales": ["取消售后申请", "仅在初始申请状态且页面提供按钮时可以取消。进入后续状态后，客户无法通过当前界面自行关闭。", "已申请"],
  "login-register": ["登录或注册账户", "使用同一个账户管理订单、仓库商品、包裹和客服。客户资料以当前登录会话为范围。", "账户访问"],
  "address-management": ["管理收货地址", "在设置中新增或编辑地址，并在包裹结账时再次确认。请准确填写收件人、电话、国家、地区、城市、街道和邮编。", "账户／包裹结账"],
  profile: ["更新个人资料", "在设置中编辑账户 API 当前支持的资料。没有编辑入口的字段请联系客服确认。", "账户"],
  notifications: ["通知", "站内通知可关联订单、包裹、仓库商品、钱包、退款或客服。请在用户中心查看并标记已读。", "完整订单流程"],
  "account-security": ["保护账户安全", "请使用独立密码、不要共享登录信息，并确认网址正确。发现异常时联系客服，切勿发送密码或完整支付凭证。", "账户"],
  disclaimers: ["服务免责声明", "卖家内容、平台可用性、质检可见范围、承运商表现和海关决定均涉及第三方，PRVBUY 不承诺其控制范围外的结果。", "下单前"],
  "prohibited-items-policy": ["禁运商品", "购买前请阅读禁运政策。承运商和目的地还可能限制敏感商品；没有安全合法线路时，PRVBUY 可要求审核或拒绝处理。", "下单前／运输"],
  "return-policy": ["退换货政策", "政策说明退货、换货和退款的适用基础；实际结果取决于商品状况、证据、时间、卖家规则和物流状态。", "购买后"],
  "shipping-terms": ["运输条款", "运输条款涵盖估算、线路选择、计费重量、限制、海关和交付责任，请在支付国际运费前阅读。", "打包／运输"],
  privacy: ["隐私政策", "请阅读隐私政策了解个人信息处理方式。不要通过客服发送密码、完整银行卡资料或无关个人文件。", "账户"],
  terms: ["服务条款", "服务条款说明使用 PRVBUY 的一般条件，请结合运输、退换和禁运政策一并阅读。", "账户／下单前"],
};

const zhLinkLabels: Record<string, string> = {
  "Contact Support": "联系客服", "Open orders": "查看订单", "View orders": "查看订单",
  "View wallet": "查看钱包", "Open wallet": "打开钱包", "View parcels": "查看包裹",
  "Open parcels": "打开包裹", "Open after-sales": "打开售后中心", "View cases": "查看售后案件",
  "Open warehouse": "打开仓库", "View warehouse items": "查看仓库商品", "Open settings": "打开设置",
  "View notifications": "查看通知", "Read policy": "阅读政策", "Register": "注册", "Log in": "登录",
};

function localizeCategories(locale: "en" | "zh") {
  if (locale === "en") return categories;
  return categories.map((category) => ({
    ...category,
    label: zhCategoryCopy[category.id]?.[0] ?? category.label,
    description: zhCategoryCopy[category.id]?.[1] ?? category.description,
    articles: category.articles.map((article) => {
      const copy = zhArticleCopy[article.id];
      return {
        ...article,
        title: copy?.[0] ?? article.title,
        summary: copy?.[1] ?? article.summary,
        answer: copy?.[1] ?? article.answer,
        stage: copy?.[2] ?? article.stage,
        links: article.links.map((link) => ({ ...link, label: zhLinkLabels[link.label] ?? "查看相关页面" })),
      };
    }),
  }));
}

const flowLinks = [
  { label: "Find an item", note: "Paste a link", href: "/", icon: Search },
  { label: "Track an order", note: "Review status", href: "/user-center/orders", icon: ClipboardCheck },
  { label: "Check warehouse", note: "QC and storage", href: "/user-center/warehouse", icon: Warehouse },
  { label: "Build a parcel", note: "Combine items", href: "/submit-packing", icon: PackageCheck },
  { label: "View parcels", note: "Recorded progress", href: "/user-center/parcels", icon: Truck },
];

export function HelpCenterClient() {
  const { locale } = useUserCenterLocale();
  const localizedCategories = useMemo(() => localizeCategories(locale), [locale]);
  const allArticles = useMemo(() => localizedCategories.flatMap((category) =>
    category.articles.map((article) => ({
      ...article,
      categoryId: category.id,
      categoryLabel: category.label,
    }))), [localizedCategories]);
  const ui = locale === "zh" ? {
    helpCenter: "帮助中心", searchLabel: "帮助搜索", searchSr: "搜索帮助中心", searchPlaceholder: "搜索帮助文章…",
    clear: "清除搜索", popular: "热门：", popularLabel: "热门搜索", popularTerms: ["质检照片", "运费估算", "退款状态"],
    journey: "您的 PRVBUY 流程", openCenter: "打开用户中心", categories: "帮助分类", browse: "浏览主题",
    articles: "篇文章", result: "条结果", results: "条结果", forQuery: "，搜索“", searchScope: "搜索范围包括文章标题、阶段和正文。",
    applies: "适用于：", noMatch: "没有找到匹配的文章", noMatchHint: "可尝试更短的关键词，例如“质检”“钱包”“打包”或“物流”。",
    browseAll: "浏览全部主题", talk: "联系 PRVBUY 客服", talkBody: "登录后可创建私密客服工单；如有相关订单、仓库商品或包裹，请一并关联。",
    contact: "联系客服", helpMeans: "帮助中心 = 操作指南", supportMeans: "客服 = 登录后提交工单并接收回复", popularTopics: "热门主题",
    privacyTitle: "保护敏感信息", privacyBody: "切勿在工单中发送密码或完整支付凭证。", notFound: "没有找到答案？", ticket: "登录后发送私密客服工单。",
    found: "篇帮助文章已找到。", inCategory: "篇文章，分类：",
  } : {
    helpCenter: "Help Center", searchLabel: "Help search", searchSr: "Search the Help Center", searchPlaceholder: "Search help articles...",
    clear: "Clear search", popular: "Popular:", popularLabel: "Popular searches", popularTerms: ["QC photos", "Shipping estimate", "Refund status"],
    journey: "Your PRVBUY journey", openCenter: "Open User Center", categories: "Help categories", browse: "Browse topics",
    articles: "articles", result: "result", results: "results", forQuery: " for “", searchScope: "Search covers article titles, stages and answers.",
    applies: "Applies to:", noMatch: "No article matched that search", noMatchHint: "Try a shorter term such as “QC”, “wallet”, “packing” or “tracking”.",
    browseAll: "Browse all topics", talk: "Talk to a PRVBUY agent", talkBody: "Sign in to create a private Support ticket. Add the relevant order, warehouse item or parcel context when available.",
    contact: "Contact Support", helpMeans: "Help Center = guidance", supportMeans: "Support = signed-in tickets and agent replies", popularTopics: "Popular topics",
    privacyTitle: "Keep sensitive details private", privacyBody: "Never send passwords or full payment credentials in a ticket.", notFound: "Couldn't find the answer?", ticket: "Sign in to send a private Support ticket.",
    found: " help articles found.", inCategory: " articles in ",
  };
  const localizedFlowLinks = locale === "zh" ? flowLinks.map((item, index) => ({
    ...item,
    ...[
      { label: "查找商品", note: "粘贴链接" }, { label: "跟踪订单", note: "查看状态" },
      { label: "查看仓库", note: "质检与仓储" }, { label: "创建包裹", note: "合并商品" },
      { label: "查看包裹", note: "跟踪进度" },
    ][index],
  })) : flowLinks;
  const [query, setQuery] = useState("");
  const [activeCategory, setActiveCategory] = useState(categories[0].id);
  const [openArticle, setOpenArticle] = useState<string | null>("what-is-prvbuy");
  const searchRef = useRef<HTMLInputElement>(null);

  const categoryIds = useMemo(() => new Set(categories.map((category) => category.id)), []);
  const articleIds = useMemo(() => new Set(allArticles.map((article) => article.id)), [allArticles]);

  useEffect(() => {
    const syncLocation = () => {
      const next = readHelpLocation(window.location.search, window.location.hash, categoryIds, articleIds);
      setQuery(next.query);
      if (next.categoryId) setActiveCategory(next.categoryId);
      if (next.articleId) {
        const article = allArticles.find((item) => item.id === next.articleId);
        if (article) {
          setActiveCategory(article.categoryId);
          setOpenArticle(article.id);
        }
      } else {
        setOpenArticle(next.query ? null : localizedCategories.find((item) => item.id === next.categoryId)?.articles[0]?.id ?? "what-is-prvbuy");
      }
    };
    syncLocation();
    window.addEventListener("popstate", syncLocation);
    window.addEventListener("hashchange", syncLocation);
    return () => {
      window.removeEventListener("popstate", syncLocation);
      window.removeEventListener("hashchange", syncLocation);
    };
  }, [allArticles, articleIds, categoryIds, localizedCategories]);

  useEffect(() => {
    const focusSearch = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLocaleLowerCase() === "k") {
        event.preventDefault();
        searchRef.current?.focus();
      }
    };
    window.addEventListener("keydown", focusSearch);
    return () => window.removeEventListener("keydown", focusSearch);
  }, []);

  const normalizedQuery = normalizeHelpQuery(query);
  const selectedCategory =
    localizedCategories.find((category) => category.id === activeCategory) ?? localizedCategories[0];

  const results = useMemo(() => {
    if (!normalizedQuery) return [];
    return searchHelpArticles(allArticles, query);
  }, [allArticles, normalizedQuery, query]);

  const visibleArticles: Array<HelpArticle & { categoryLabel?: string }> =
    normalizedQuery ? results : selectedCategory.articles;
  const popularArticles = allArticles.filter((article) => article.popular).slice(0, 6);

  const replaceLocation = (next: { query?: string; categoryId?: string | null; articleId?: string | null }) => {
    window.history.replaceState(
      null,
      "",
      buildHelpLocation({
        query: next.query ?? query,
        categoryId: next.categoryId === undefined ? activeCategory : next.categoryId,
        articleId: next.articleId ?? null,
      }),
    );
  };

  const clearSearch = () => {
    setQuery("");
    const firstArticle = selectedCategory.articles[0]?.id ?? null;
    setOpenArticle(firstArticle);
    replaceLocation({ query: "", categoryId: selectedCategory.id, articleId: null });
    searchRef.current?.focus();
  };

  const selectCategory = (categoryId: string) => {
    setQuery("");
    setActiveCategory(categoryId);
    const category = localizedCategories.find((item) => item.id === categoryId);
    setOpenArticle(category?.articles[0]?.id ?? null);
    window.history.replaceState(null, "", buildHelpLocation({ query: "", categoryId, articleId: null }));
    document
      .getElementById("help-articles")
      ?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const openPopularArticle = (articleId: string, categoryId: string) => {
    setQuery("");
    setActiveCategory(categoryId);
    setOpenArticle(articleId);
    window.history.replaceState(null, "", buildHelpLocation({ query: "", categoryId, articleId }));
    document
      .getElementById("help-articles")
      ?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <main className="help-center">
      <section className="help-hero" aria-label={ui.searchLabel}>
        <div className="help-container help-hero-inner">
          <h1>{ui.helpCenter}</h1>
          <label className="help-search">
            <Search aria-hidden="true" />
            <span className="sr-only">{ui.searchSr}</span>
            <input
              ref={searchRef}
              type="search"
              value={query}
              onChange={(event) => {
                setQuery(event.target.value);
                setOpenArticle(null);
                replaceLocation({ query: event.target.value, articleId: null });
              }}
              onKeyDown={(event) => {
                if (event.key === "Escape" && query) {
                  event.preventDefault();
                  clearSearch();
                }
              }}
              placeholder={ui.searchPlaceholder}
              aria-describedby="help-search-status"
            />
            {query ? (
              <button
                type="button"
                onClick={() => {
                  clearSearch();
                }}
                aria-label={ui.clear}
              >
                <X aria-hidden="true" />
              </button>
            ) : (
              <kbd>⌘ K</kbd>
            )}
          </label>
          <div className="help-search-hints" aria-label={ui.popularLabel}>
            <span>{ui.popular}</span>
            {ui.popularTerms.map((term) => (
              <button
                type="button"
                key={term}
                onClick={() => {
                  setQuery(term);
                  setOpenArticle(null);
                  replaceLocation({ query: term, articleId: null });
                }}
              >
                {term}
              </button>
            ))}
          </div>
        </div>
      </section>

      <section className="help-flow help-container" aria-labelledby="flow-title">
        <div className="help-section-heading">
          <div>
            <h2 id="flow-title">{ui.journey}</h2>
          </div>
          <Link href="/user-center">
            {ui.openCenter} <ArrowRight aria-hidden="true" />
          </Link>
        </div>
        <div className="help-flow-grid">
          {localizedFlowLinks.map((item, index) => {
            const Icon = item.icon;
            return (
              <Link href={item.href} key={item.label} className="help-flow-card">
                <span className="help-flow-step">{String(index + 1).padStart(2, "0")}</span>
                <Icon aria-hidden="true" />
                <span>
                  <strong>{item.label}</strong>
                  <small>{item.note}</small>
                </span>
                <ArrowRight aria-hidden="true" />
              </Link>
            );
          })}
        </div>
      </section>

      <section className="help-mobile-categories" aria-label={ui.categories}>
        <div>
          {localizedCategories.map((category) => {
            const Icon = category.icon;
            return (
              <button
                type="button"
                key={category.id}
                aria-pressed={!normalizedQuery && activeCategory === category.id}
                onClick={() => selectCategory(category.id)}
              >
                <Icon aria-hidden="true" />
                {category.label}
              </button>
            );
          })}
        </div>
      </section>

      <section className="help-layout help-container" id="help-articles">
        <nav className="help-category-nav" aria-label={ui.categories}>
          <h2>{ui.browse}</h2>
          {localizedCategories.map((category) => {
            const Icon = category.icon;
            const selected = !normalizedQuery && activeCategory === category.id;
            return (
              <button
                type="button"
                key={category.id}
                className={selected ? "is-active" : ""}
                aria-current={selected ? "page" : undefined}
                onClick={() => selectCategory(category.id)}
              >
                <Icon aria-hidden="true" />
                <span>
                  <strong>{category.label}</strong>
                  <small>{category.articles.length} {ui.articles}</small>
                </span>
                <ArrowRight aria-hidden="true" />
              </button>
            );
          })}
        </nav>

        <article className="help-article-panel">
          <header className="help-article-header">
            <div>
              <h2>
                {normalizedQuery
                  ? locale === "zh"
                    ? `${results.length}${ui.results}${ui.forQuery}${query.trim()}”`
                    : `${results.length} ${results.length === 1 ? ui.result : ui.results}${ui.forQuery}${query.trim()}”`
                  : selectedCategory.label}
              </h2>
              <span>
                {normalizedQuery
                  ? ui.searchScope
                  : selectedCategory.description}
              </span>
            </div>
            {!normalizedQuery ? (
              <span className="help-article-count">
                {selectedCategory.articles.length}
                <small>{ui.articles}</small>
              </span>
            ) : null}
          </header>
          <span className="sr-only" id="help-search-status" aria-live="polite">
            {normalizedQuery
              ? locale === "zh" ? `${results.length}${ui.found}` : `${results.length}${ui.found}`
              : locale === "zh" ? `${selectedCategory.articles.length}${ui.inCategory}${selectedCategory.label}。` : `${selectedCategory.articles.length}${ui.inCategory}${selectedCategory.label}.`}
          </span>

          {visibleArticles.length ? (
            <div className="help-accordion">
              {visibleArticles.map((article) => {
                const isOpen = openArticle === article.id;
                return (
                  <section
                    className={`help-accordion-item${isOpen ? " is-open" : ""}`}
                    key={article.id}
                  >
                    <button
                      type="button"
                      id={`article-trigger-${article.id}`}
                      aria-expanded={isOpen}
                      aria-controls={`article-${article.id}`}
                      onClick={() => {
                        const nextArticle = isOpen ? null : article.id;
                        const articleCategory = allArticles.find((item) => item.id === article.id)?.categoryId ?? activeCategory;
                        setOpenArticle(nextArticle);
                        replaceLocation({ query, categoryId: articleCategory, articleId: nextArticle });
                      }}
                    >
                      <span>
                        {normalizedQuery && article.categoryLabel ? (
                          <small>{article.categoryLabel}</small>
                        ) : null}
                        <strong>{article.title}</strong>
                        <em>{article.summary}</em>
                      </span>
                      <ChevronDown aria-hidden="true" />
                    </button>
                    <div
                      id={`article-${article.id}`}
                      role="region"
                      aria-labelledby={`article-trigger-${article.id}`}
                      hidden={!isOpen}
                    >
                      <div className="help-article-body">
                        <span className="help-stage">
                          <Landmark aria-hidden="true" />
                          {ui.applies} {article.stage}
                        </span>
                        <p>{article.answer}</p>
                        <div className="help-related-links">
                          {article.links.map((link) => (
                            <Link href={link.href} key={`${article.id}-${link.href}`}>
                              {link.label}
                              <ArrowRight aria-hidden="true" />
                            </Link>
                          ))}
                        </div>
                      </div>
                    </div>
                  </section>
                );
              })}
            </div>
          ) : (
            <div className="help-empty">
              <Search aria-hidden="true" />
              <h3>{ui.noMatch}</h3>
              <p>{ui.noMatchHint}</p>
              <button type="button" onClick={clearSearch}>
                {ui.browseAll}
              </button>
            </div>
          )}
        </article>

        <aside className="help-aside">
          <section className="help-contact-card">
            <span className="help-contact-icon">
              <Headphones aria-hidden="true" />
            </span>
            <h2>{ui.talk}</h2>
            <span>{ui.talkBody}</span>
            <Link href="/user-center/support">
              {ui.contact} <ArrowRight aria-hidden="true" />
            </Link>
            <small>
              {ui.helpMeans}
              <br />
              {ui.supportMeans}
            </small>
          </section>

          <section className="help-popular">
            <h2>{ui.popularTopics}</h2>
            {popularArticles.map((article, index) => (
              <button
                type="button"
                key={article.id}
                onClick={() => openPopularArticle(article.id, article.categoryId)}
              >
                <span>{String(index + 1).padStart(2, "0")}</span>
                {article.title}
                <ArrowRight aria-hidden="true" />
              </button>
            ))}
          </section>

          <section className="help-safety-note">
            <ShieldCheck aria-hidden="true" />
            <div>
              <strong>{ui.privacyTitle}</strong>
              <span>{ui.privacyBody}</span>
            </div>
          </section>
        </aside>
      </section>

      <section className="help-mobile-contact help-container">
        <div>
          <Headphones aria-hidden="true" />
          <span>
            <strong>{ui.notFound}</strong>
            <small>{ui.ticket}</small>
          </span>
        </div>
        <Link href="/user-center/support">
          {ui.contact} <ArrowRight aria-hidden="true" />
        </Link>
      </section>
    </main>
  );
}
